import React from 'react'
import PropTypes from 'prop-types'
import './EmptyState.scss'

/**
 * EmptyState - Displays a message when there's no data to show
 *
 * @param {string} icon - Icon type or custom icon element
 * @param {string} title - Primary message
 * @param {string} description - Secondary descriptive text
 * @param {React.ReactNode} action - Call-to-action button/element
 * @param {string} size - Size variant (small, medium, large)
 * @param {string} variant - Style variant (default, muted, card)
 */
const EmptyState = ({
	icon = 'default',
	title = 'No data found',
	description,
	action,
	size = 'medium',
	variant = 'default'
}) => {
	const getIcon = () => {
		if (React.isValidElement(icon)) return icon

		const icons = {
			projects: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z" />
				</svg>
			),
			tasks: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
				</svg>
			),
			activity: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
				</svg>
			),
			notifications: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
				</svg>
			),
			documents: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z" />
				</svg>
			),
			team: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
				</svg>
			),
			calendar: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
				</svg>
			),
			approvals: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
				</svg>
			),
			search: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
				</svg>
			),
			default: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-1 12H5c-.55 0-1-.45-1-1V9c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v8c0 .55-.45 1-1 1z" />
				</svg>
			)
		}

		return icons[icon] || icons.default
	}

	return (
		<div className={`empty-state empty-state--${size} empty-state--${variant}`}>
			<div className="empty-state__icon">
				{getIcon()}
			</div>

			<h4 className="empty-state__title">{title}</h4>

			{description && (
				<p className="empty-state__description">{description}</p>
			)}

			{action && (
				<div className="empty-state__action">
					{action}
				</div>
			)}
		</div>
	)
}

EmptyState.propTypes = {
	icon: PropTypes.oneOfType([
		PropTypes.node,
		PropTypes.oneOf([
			'projects',
			'tasks',
			'activity',
			'notifications',
			'documents',
			'team',
			'calendar',
			'approvals',
			'search',
			'default'
		])
	]),
	title: PropTypes.string,
	description: PropTypes.string,
	action: PropTypes.node,
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	variant: PropTypes.oneOf(['default', 'muted', 'card'])
}

export default EmptyState
